///
/// EarthBrowser
///
/// SQL Terminal Browser Feature
///
/// Manages SQL Terminal window lifecycle and command palette registration (dev mode only)
///
/// Created by Matt Giger on December 19, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useEffect } from 'react'
import { Terminal } from 'lucide-react'
import { SQLTerminalBrowserRenderer } from '@/components/features/sql-terminal-browser/SQLTerminalBrowser'
import { CommandRegistry } from '@/components/features/CommandRegistry'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { isDevelopment } from '@/utils/environment'
import { openOrRestoreWindow } from '@/utils/windowUtils'

const SQL_TERMINAL_WINDOW_ID = 'sql-terminal-browser'

interface SQLTerminalBrowserFeatureProps {
	devtoolsUnlocked: boolean
}

export function SQLTerminalBrowserFeature({ devtoolsUnlocked }: SQLTerminalBrowserFeatureProps) {
	const dispatch = useAppDispatch()
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const sqlTerminalWindow = datasetWindows[SQL_TERMINAL_WINDOW_ID]

	// Register with command palette (only in dev mode)
	// Shows in dev mode OR when unlocked via long press
	useEffect(() => {
		const shouldShow = isDevelopment() || devtoolsUnlocked
		if (!shouldShow) return

		const unregister = CommandRegistry.registerFeature({
			id: 'sql-terminal-browser',
			command: {
				id: 'sql-terminal',
				title: 'SQL Terminal',
				icon: <Terminal className="w-4 h-4 text-yellow-500" />,
				category: 'Developer',
				enabled: true,
		onSelect: () => {
			openOrRestoreWindow(
				dispatch,
				datasetWindows,
				SQL_TERMINAL_WINDOW_ID,
				{
					id: SQL_TERMINAL_WINDOW_ID,
					title: 'SQL Terminal',
					windowType: 'sql-terminal',
					x: 150,
					y: 150,
					width: 900,
					height: 700,
					isMaximized: false,
					isVisible: true,
				}
			)
		}
			},
			renderBrowser: () => null // Not needed for window-based browser
		})

		return unregister
	}, [dispatch, devtoolsUnlocked, datasetWindows])

	// Render the window if it's open
	if (!sqlTerminalWindow) {
		return null
	}

	return (
		<SQLTerminalBrowserRenderer
			windowId={SQL_TERMINAL_WINDOW_ID}
			windowState={sqlTerminalWindow}
		/>
	)
}

